/**
 * Live re-key marker reads against the hosted UI's /oauth2/userInfo — the one marker source that
 * needs nothing but a valid access token, on every surface this SPA runs on. Both endpoints used
 * here answer CORS for the SPA origin and are already in the platform CSP.
 */

import { decodeJwtPayload } from '@utils/jwtUtils'
import { parseRekeyMarker, AUTHZ_REKEY_ATTRIBUTE, OLD_SUB_ATTRIBUTE, RekeyMarkerState } from './rekeySignal'

export type RekeyProbeFailureKind = 'network' | 'timeout' | 'unauthorized' | 'http'

export class RekeyProbeError extends Error {
    public readonly kind: RekeyProbeFailureKind
    public readonly status?: number

    constructor (kind: RekeyProbeFailureKind, message: string, status?: number) {
        super(message)
        this.name = 'RekeyProbeError'
        this.kind = kind
        this.status = status
    }
}

/** `oldSubPresent` lets the no-claims (in-app) path tell mid-migration from never-migrated. */
export interface IRekeyProbeResult {
    marker: RekeyMarkerState
    oldSubPresent: boolean
}

export type FetchLike = (url: string, init: { method: string; headers?: Record<string, string>; signal: AbortSignal }) => Promise<{
    ok: boolean,
    status: number,
    json (): Promise<any>,
}>

// Browser fetch has no default deadline, so every request here is abort-raced: a hung endpoint
// must cost one tolerated poll error, never a wedged gate.
export const PROBE_TIMEOUT_MS = 10000

const COGNITO_ISSUER_PATTERN = /^https:\/\/cognito-idp\.[a-z0-9-]+\.amazonaws\.com\/[A-Za-z0-9_-]+$/

const discoveryCache = new Map<string, string>()

/**
 * Resolves the /oauth2/userInfo URL: from the web session's tokenEndpoint origin when present,
 * else (in-WebView, access token only) from the pool's public OIDC discovery document via the
 * token's `iss`. Discovery is cached per issuer — the document is static per pool.
 */
export async function resolveUserInfoEndpoint (
    options: { tokenEndpoint?: string | null; accessToken: string },
    fetchFn: FetchLike = fetch,
    timeoutMs: number = PROBE_TIMEOUT_MS,
): Promise<string> {
    if (options.tokenEndpoint) {
        return `${new URL(options.tokenEndpoint).origin}/oauth2/userInfo`
    }

    const issuer: unknown = decodeJwtPayload(options.accessToken)?.iss
    // Only a Cognito user-pool issuer is a legitimate discovery target: the iss comes from an
    // unverified JWT payload, so pin the host shape before fetching anything from it.
    if (typeof issuer !== 'string' || !COGNITO_ISSUER_PATTERN.test(issuer)) {
        throw new RekeyProbeError('http', '[AccountSetup] Access token carries no usable Cognito iss claim')
    }
    const cached = discoveryCache.get(issuer)
    if (cached) {
        return cached
    }

    const document = await requestJson(`${issuer}/.well-known/openid-configuration`, undefined, fetchFn, timeoutMs)
    // OIDC Discovery §4.3: the document must claim the issuer it was fetched for.
    if (document?.issuer !== issuer) {
        throw new RekeyProbeError('http', '[AccountSetup] OIDC discovery document issuer mismatch')
    }
    const endpoint: unknown = document?.userinfo_endpoint
    if (typeof endpoint !== 'string' || !endpoint.startsWith('https://')) {
        throw new RekeyProbeError('http', '[AccountSetup] OIDC discovery document has no userinfo_endpoint')
    }
    discoveryCache.set(issuer, endpoint)
    return endpoint
}

/** Test hook. */
export function clearUserInfoEndpointCache (): void {
    discoveryCache.clear()
}

/**
 * Reads the migration attributes. Missing attributes are a normal answer, not an error; every
 * failure is a typed RekeyProbeError so the controller can count it by kind.
 */
export async function fetchRekeyMarker (
    userInfoEndpoint: string,
    accessToken: string,
    fetchFn: FetchLike = fetch,
    timeoutMs: number = PROBE_TIMEOUT_MS,
): Promise<IRekeyProbeResult> {
    const attributes = await requestJson(userInfoEndpoint, { Authorization: `Bearer ${accessToken}` }, fetchFn, timeoutMs)
    const oldSub: unknown = attributes?.[OLD_SUB_ATTRIBUTE]
    return {
        marker: parseRekeyMarker(attributes?.[AUTHZ_REKEY_ATTRIBUTE]),
        oldSubPresent: typeof oldSub === 'string' && oldSub.trim() !== '',
    }
}

/** GET + JSON with an abort-raced deadline; failures normalized to RekeyProbeError kinds. */
async function requestJson (
    url: string,
    headers: Record<string, string> | undefined,
    fetchFn: FetchLike,
    timeoutMs: number,
): Promise<any> {
    const abortController = new AbortController()
    let timedOut = false
    const timer = setTimeout(() => {
        timedOut = true
        abortController.abort()
    }, timeoutMs)
    try {
        let response
        try {
            response = await fetchFn(url, { method: 'GET', headers, signal: abortController.signal })
        } catch (error) {
            if (timedOut) {
                throw new RekeyProbeError('timeout', `[AccountSetup] Request to ${url} timed out after ${timeoutMs}ms`)
            }
            throw new RekeyProbeError('network', `[AccountSetup] Request to ${url} failed: ${(error as Error)?.message ?? error}`)
        }
        if (response.status === 401) {
            throw new RekeyProbeError('unauthorized', `[AccountSetup] ${url} rejected the access token`, 401)
        }
        if (!response.ok) {
            throw new RekeyProbeError('http', `[AccountSetup] ${url} answered ${response.status}`, response.status)
        }
        try {
            return await response.json()
        } catch (error) {
            if (timedOut) {
                throw new RekeyProbeError('timeout', `[AccountSetup] Reading ${url} timed out after ${timeoutMs}ms`)
            }
            throw new RekeyProbeError('http', `[AccountSetup] ${url} returned an unreadable body`)
        }
    } finally {
        clearTimeout(timer)
    }
}
